/**
 * Note Severity Tagger
 *
 * Classifies care notes by severity based on keyword presence and image attachments.
 * Priority order (first match wins):
 *   1. Image Attached — note has an image URL or image ID
 *   2. Clinical Attention — text contains clinical keywords
 *   3. Refused — text contains refusal keywords
 *   4. Partial Intake — text contains partial intake keywords
 *   5. Information — default fallback
 */

import { NormalizedNoteRecord, SeverityTag } from '../types';

const CLINICAL_ATTENTION_PATTERN = /pain|wound|bleeding|fever|fall|dizzy|faint|doctor/;
const REFUSED_PATTERN = /refused|declined|reject/;
const PARTIAL_INTAKE_PATTERN = /half|few|partial/;

/**
 * Classify a note record's severity based on keyword presence and image attachment.
 *
 * @param note - A normalized note record to classify
 * @returns The highest-priority severity tag that matches
 */
export function classifyNoteSeverity(note: NormalizedNoteRecord): SeverityTag {
  const text = (note.noteText || '').toLowerCase();

  if (note.imageUrl || note.imageId) return 'Image Attached';
  if (CLINICAL_ATTENTION_PATTERN.test(text)) return 'Clinical Attention';
  if (REFUSED_PATTERN.test(text)) return 'Refused';
  if (PARTIAL_INTAKE_PATTERN.test(text)) return 'Partial Intake';
  return 'Information';
}
